<template>
  <div v-loading="loading" class="beauty-ai-360-analysis">
    <div>
      <Member360AnalysisChart
        :data="displayData"
      />
    </div>
    <div class="flex flex-col" style="gap: 48px">
      <div>
        <SectionTitle
          :title="$t('member.tab.beautyAI360Analysis.analysis360')"
          :btn="$t('member.tab.beautyAI360Analysis.help')"
          @edit="modal.help = true"
        />
        <div class="flex items-center flex-wrap" style="gap: 32px; margin-top: 20px">
          <div v-for="item in displayData.score" :key="item.label" class="flex" style="gap: 12px">
            <p class="flex-shrink-0 font-medium">{{ item.label }}</p>
            <p class="text-primary-100 font-bold ">{{ item.value }}</p>
          </div>
        </div>
      </div>

      <div>
        <SectionTitle :title="$t('member.tab.beautyAI360Analysis.aiCustomerAnalysis')" hideBtn />
        <div class="flex items-start" style="margin-top: 20px; margin-bottom: 20px; gap: 8px">
          <p class="font-medium text-sub flex-shrink-0">{{ $t('member.tab.beautyAI360Analysis.aiSmartTags') }}</p>
          <div v-if="aiTags.length" class="flex items-center flex-wrap" style="gap: 8px">
            <Tag v-for="tag in aiTags" :key="tag.id" type="action" size="small">
              {{ tag.name }}
            </Tag>
          </div>
          <div v-else>
            <p class="text-sub text-gray-60">{{ $t('member.tab.beautyAI360Analysis.noInteraction') }}</p>
          </div>
        </div>

        <div class="text-sub text-gray-100">
          <p v-if="!aiTags.length">{{ displayData.description }}</p>

          <!-- 常來貴客 -->
          <div v-if="find(aiTags, {systemName: 'beautyAIMTag_frequentCustomer'})">
            <p>{{ $t('member.tab.beautyAI360Analysis.tagDescFrequentCustomer1') }}</p>
            <!-- <br> -->
            <p>{{ $t('member.tab.beautyAI360Analysis.tagDescFrequentCustomer2') }}</p>
          </div>
          <!-- 活躍熟客 -->
          <div v-if="find(aiTags, {systemName: 'beautyAIMTag_activeCustomer'})">
            <p>{{ $t('member.tab.beautyAI360Analysis.tagDescActiveCustomer1') }}</p>
            <!-- <br> -->
            <p>{{ $t('member.tab.beautyAI360Analysis.tagDescActiveCustomer2') }}</p>
          </div>
          <!-- 低調大戶 -->
          <div v-if="find(aiTags, {systemName: 'beautyAIMTag_lowKeyBigSpender'})">
            <p>{{ $t('member.tab.beautyAI360Analysis.tagDescLowKeyBigSpender1') }}</p>
            <!-- <br> -->
            <p>{{ $t('member.tab.beautyAI360Analysis.tagDescLowKeyBigSpender2') }}</p>
          </div>
          <!-- 偶爾光顧 -->
          <div v-if="find(aiTags, {systemName: 'beautyAIMTag_occasionalCustomer'})">
            <p>{{ $t('member.tab.beautyAI360Analysis.tagDescOccasionalCustomer1') }}</p>
            <!-- <br> -->
            <p>{{ $t('member.tab.beautyAI360Analysis.tagDescOccasionalCustomer2') }}</p>
          </div>
          <!-- 潛力大戶 -->
          <div v-if="find(aiTags, {systemName: 'beautyAIMTag_potentialBigSpender'})">
            <p>{{ $t('member.tab.beautyAI360Analysis.tagDescPotentialBigSpender1') }}</p>
            <!-- <br> -->
            <p>{{ $t('member.tab.beautyAI360Analysis.tagDescPotentialBigSpender2') }}</p>
          </div>
          <!-- 潛力活躍客 -->
          <div v-if="find(aiTags, {systemName: 'beautyAIMTag_potentialActiveCustomer'})">
            <p>{{ $t('member.tab.beautyAI360Analysis.tagDescPotentialActiveCustomer1') }}</p>
            <!-- <br> -->
            <p>{{ $t('member.tab.beautyAI360Analysis.tagDescPotentialActiveCustomer2') }}</p>
          </div>
          <!-- 潛水大戶 -->
          <div v-if="find(aiTags, {systemName: 'beautyAIMTag_deepDiveBigSpender'})">
            <p>{{ $t('member.tab.beautyAI360Analysis.tagDescDeepDiveBigSpender1') }}</p>
            <!-- <br> -->
            <p>{{ $t('member.tab.beautyAI360Analysis.tagDescDeepDiveBigSpender2') }}</p>
          </div>
          <!-- 潛水客 -->
          <div v-if="find(aiTags, {systemName: 'beautyAIMTag_deepDiveCustomer'})">
            <p>{{ $t('member.tab.beautyAI360Analysis.tagDescDeepDiveCustomer1') }}</p>
            <!-- <br> -->
            <p>{{ $t('member.tab.beautyAI360Analysis.tagDescDeepDiveCustomer2') }}</p>
          </div>
        </div>
      </div>
    </div>

    <Member3660AnalysisDescriptionModal
      v-if="modal.help"
      @close="modal.help = false"
    />
  </div>
</template>

<script>
import { computed, defineComponent, onBeforeMount, reactive, ref } from 'vue'
import SectionTitle from '@/components/Title/SectionTitle.vue'
import Member360AnalysisChart from './components/Member360AnalysisChart.vue'
import Member3660AnalysisDescriptionModal from './components/Member3660AnalysisDescriptionModal.vue'
import { GetBeautyAIMemberAnalysis360View } from '@/api/beautyAI'
import { useShop } from '@/use/shop'
import { get, filter, find } from 'lodash'
import { t } from '@/plugins/i18n/i18n'

export default defineComponent({
  name: 'BeautyAI360Analysis',
  components: { SectionTitle, Member360AnalysisChart, Member3660AnalysisDescriptionModal },
  props: {
    member: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  setup (props) {
    const { shopId } = useShop()
    const memberAnalysisData = ref({})
    const loading = ref(true)
    const modal = reactive({
      help: false,
    })
    const aiTags = computed(() => {
      return filter(get(props.member, 'SystemMTag'), (tag) => tag.systemGroup === 'beautyAIMTag')
    })
    const displayData = computed(() => {
      const metricsData = get(memberAnalysisData.value, 'metrics', {})
      return {
        metricsData,
        score: [
          { label: t('member.tab.beautyAI360Analysis.scoreContribution'), value: get(metricsData, 'contributionStar') || '-' },
          { label: t('member.tab.beautyAI360Analysis.scoreDispersion'), value: get(metricsData, 'dispersionStar') || '-' },
          { label: t('member.tab.beautyAI360Analysis.scoreActivity'), value: get(metricsData, 'activityStar') || '-' },
          { label: t('member.tab.beautyAI360Analysis.scorePotential'), value: get(metricsData, 'potentialStar') || '-' },
          { label: t('member.tab.beautyAI360Analysis.scoreLoyalty'), value: get(metricsData, 'loyaltyStar') || '-' },
        ],
        tags: [],
        description: t('member.tab.beautyAI360Analysis.noDataYet'),
      }
    })

    const refresh = async () => {
      loading.value = true
      const [res, err] = await GetBeautyAIMemberAnalysis360View({
        shopId: shopId.value,
        memberId: props.member.id,
      })
      loading.value = false
      if (err) {
        window.$message.error(err)
        return
      }
      memberAnalysisData.value = res
    }

    onBeforeMount(async () => {
      refresh()
    })

    return { displayData, modal, memberAnalysisData, loading, aiTags, find }
  },
})
</script>

<style lang="postcss" scoped>
.beauty-ai-360-analysis {
  @apply bg-white rounded-[4px] p-[40px];
  @apply flex justify-between gap-[60px];
}
</style>
